import React from "react"
import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  Grid
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { useFormikContext } from "formik"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons"
import { TemplateFormData } from "../CreateTemplateModal.interfaces"
import { DynamicTooltip } from "components/Tooltip/DynamicTooltip"
import { StyledRowGapBox } from "shared/styled/General"
import { StyledInputLabel } from "components/Form/FormComponents/FormSwitch/FormSwitch.styled"

interface BasicSettingsStepProps {
  setSessionServiceValues: React.Dispatch<React.SetStateAction<any>>
}

const basic_top_desc = "Choose how the next check should be calculated - based on date, mileage or inspection.         Date and mileage can be combined, but inspection based cannot be used with the other types."

const inspectionTypeDesc = "The inspection date is automatically retrieved from the Swedish Transport Agency via registration number. No manual range entry required. This option cannot be combined with manual intervals."

export const BasicSettingsStep: React.FC<BasicSettingsStepProps> = ({ setSessionServiceValues }) => {
  const formik = useFormikContext<TemplateFormData>()
  const theme = useTheme()
  const { t } = useTranslation()

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box>
        <StyledInputLabel>
          <Typography variant="h6" color="text.primary">{t("global.name", "Name")}</Typography>
        </StyledInputLabel>
        <TextField
          sx={{ m: 0 }}
          fullWidth
          name="name"
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
          margin="normal"
        />
      </Box>

      <StyledRowGapBox sx={{ gap: 1 }}>
        <Typography variant="h4">
          {t("global.checktypes", "Check type")}
        </Typography>
        <Tooltip
          title={t("checks.basic_settings_desc", basic_top_desc)}>
          <FontAwesomeIcon
            size="lg"
            icon={faCircleInfo}
            color={theme.palette["primary"].dark}
          />
        </Tooltip>
      </StyledRowGapBox>

      {formik.touched.usesDate &&
        formik.touched.usesDistance &&
        formik.touched.usesInspection &&
        !formik.values.usesDate &&
        !formik.values.usesDistance &&
        !formik.values.usesInspection && (
          <Alert variant="outlined" severity="error" sx={{ mt: 2 }}>
            {t("checks.choose_check_type", "You must select at least one check type (either manual intervals or inspection)")}
          </Alert>
        )}

      <FormGroup>
        <List sx={{ p: 0, display: "flex", flexDirection: "column", gap: 1 }}>
          <ListItem disablePadding>
            <Grid direction="column">
              <StyledRowGapBox sx={{ gap: 1 }}>
                <FormControlLabel
                  label={t("global.date_based", "Date based")}
                  sx={{
                    opacity: formik.values.usesInspection ? 0.5 : 1,
                    display: "flex",
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": {
                      color: formik.values.usesInspection ? "text.disabled" : "inherit",
                    }
                  }}
                  control={
                    <Checkbox
                      name="usesDate"
                      checked={formik.values.usesDate}
                      disabled={formik.values.usesInspection}
                      onChange={(e) => {
                        if (e.target.checked) {
                          formik.setFieldValue("usesInspection", false)
                        }
                        formik.handleChange(e)
                      }}
                      sx={{ paddingBottom: 0 }}
                    />
                  }
                />
                {formik.values.usesDate && !formik.values.usesInspection && <Tooltip
                  title={formik.values.dateCalculationType === "Dynamic"
                    ? `${t("global.dynamic", "Dynamic")}: ${t("checks.next_confirm_info", "Next service is based on confirmed date")}`
                    : `${t("global.static", "Static")}: ${t("checks.next_confirm_static", "The next service is based on the previous deadline")}`}
                >
                  <FontAwesomeIcon
                    size="lg"
                    icon={faCircleInfo}
                    color={theme.palette["primary"].dark}
                  />
                </Tooltip>}
              </StyledRowGapBox>

              {!formik.values.usesDate && (
                <Typography ml={4} color="text.secondary">
                  {t("checks.days_between_checks", "Calculated based on days between checks.")}
                </Typography>
              )}
              {formik.values.usesInspection && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 4, fontStyle: "italic" }}>
                  {t("checks.not_available_inspection", "Not available when inspection is selected")}
                </Typography>
              )}
              {formik.values.usesDate && !formik.values.usesInspection && (
                <StyledRowGapBox sx={{ ml: 4 }}>
                  <TextField
                    type="number"
                    label={`${t("global.interval", "Interval")} ${t("global.days", "days")}`}
                    name="dateInterval"
                    value={formik.values.dateInterval}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.dateInterval && Boolean(formik.errors.dateInterval)}
                    helperText={formik.touched.dateInterval && formik.errors.dateInterval}
                    margin="normal"
                    sx={{ maxWidth: 200 }}
                  />
                  <StyledRowGapBox sx={{ gap: 1 }}>
                    <FormControl margin="normal" sx={{ minWidth: 150 }}>
                      <InputLabel>{t("global.calculation_type", "Inspection type")}</InputLabel>
                      <Select
                        name="dateCalculationType"
                        value={formik.values.dateCalculationType}
                        onChange={formik.handleChange}
                        label={t("global.calculation_type", "Calculation type")}
                      >
                        <MenuItem value="Dynamic">{t("global.dynamic", "Dynamic")}</MenuItem>
                        <MenuItem value="Static">{t("global.static", "Static")}</MenuItem>
                      </Select>
                    </FormControl>
                  </StyledRowGapBox>
                </StyledRowGapBox>
              )}
            </Grid>
          </ListItem>
          <ListItem disablePadding>
            <Grid direction="column">
              <StyledRowGapBox sx={{ gap: 1 }}>
                <FormControlLabel
                  label={t("global.distance_based", "Distance based")}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    opacity: formik.values.usesInspection ? 0.5 : 1,
                    "& .MuiFormControlLabel-label": {
                      color: formik.values.usesInspection ? "text.disabled" : "inherit",
                    }
                  }}
                  control={
                    <Checkbox
                      name="usesDistance"
                      checked={formik.values.usesDistance}
                      disabled={formik.values.usesInspection}
                      onChange={(e) => {
                        if (e.target.checked) {
                          formik.setFieldValue("usesInspection", false)
                        }
                        formik.handleChange(e)
                      }}
                      sx={{ paddingBottom: 0 }}
                    />
                  }
                />
                {formik.values.usesDistance && !formik.values.usesInspection && <Tooltip
                  title={formik.values.distanceCalculationType === "Dynamic"
                    ? `${t("global.dynamic", "Dynamic")}: ${t("checks.next_confirm_distance_dynamic", "Next service is based on confirmed distance")}`
                    : `${t("global.static", "Static")}: ${t("checks.next_confirm_distance_static", "The next service is based on the previous deadline")}`}
                  placement="top"
                >
                  <FontAwesomeIcon
                    size={"lg"}
                    icon={faCircleInfo}
                    color={theme.palette["primary"].dark}
                  />
                </Tooltip>}
              </StyledRowGapBox>

              {!formik.values.usesDistance && (
                <Typography ml={4} color="text.secondary">
                  {t("checks.calculated_interval", "Calculated based on distance interval.")}
                </Typography>
              )}

              {formik.values.usesInspection && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 4, fontStyle: "italic" }}>
                  {t("checks.not_available_when_inspection", "Not available when inspection is selected")}
                </Typography>
              )}
              {formik.values.usesDistance && !formik.values.usesInspection && (
                <StyledRowGapBox sx={{ ml: 4, gap: 2 }}>
                  <TextField
                    type="number"
                    label={`${t("global.interval", "Interval")} (km)`}
                    name="distanceInterval"
                    value={formik.values.distanceInterval}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.distanceInterval && Boolean(formik.errors.distanceInterval)}
                    helperText={formik.touched.distanceInterval && formik.errors.distanceInterval}
                    margin="normal"
                    sx={{ maxWidth: 200 }}
                  />
                  <StyledRowGapBox sx={{ gap: 3 }}>
                    <FormControl margin="normal" sx={{ minWidth: 150 }}>
                      <InputLabel>{t("global.calculation_type", "Calculation type")}</InputLabel>
                      <Select
                        name="distanceCalculationType"
                        value={formik.values.distanceCalculationType}
                        onChange={formik.handleChange}
                        label={t("global.calculation_type", "Calculation type")}
                      >
                        <MenuItem value="Dynamic">{t("global.dynamic", "Dynamic")}</MenuItem>
                        <MenuItem value="Static">{t("global.static", "Static")}</MenuItem>
                      </Select>
                    </FormControl>

                  </StyledRowGapBox>
                </StyledRowGapBox>
              )}
            </Grid>
          </ListItem>
          <ListItem disablePadding>
            <Grid direction="column">
              <FormControlLabel
                label={t("global.inspection_based", "Inspection based")}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  opacity: (formik.values.usesDate || formik.values.usesDistance) ? 0.5 : 1,
                  "& .MuiFormControlLabel-label": {
                    color: (formik.values.usesDate || formik.values.usesDistance) ? "text.disabled" : "inherit",
                  }
                }}
                control={
                  <Checkbox
                    name="usesInspection"
                    checked={formik.values.usesInspection}
                    disabled={formik.values.usesDate || formik.values.usesDistance}
                    onChange={(e) => {
                      if (e.target.checked) {
                        formik.setFieldValue("usesDate", false)
                        formik.setFieldValue("usesDistance", false)
                        formik.setFieldValue("dateInterval", 0)
                        formik.setFieldValue("distanceInterval", 0)
                        formik.setFieldValue("vehicleLastValues", {})
                        setSessionServiceValues({})
                      }
                      formik.handleChange(e)
                    }}
                    sx={{ paddingBottom: 0 }}
                  />
                }
              />

              {!formik.values.usesInspection && (!formik.values.usesDate && !formik.values.usesDistance) && (
                <>
                  <Typography ml={4} color="text.secondary">
                    {t("checks.calculation_transportstyrelsen", "Calculated based on the next inspection date from the Swedish Transport Agency.")}
                  </Typography>
                  <Typography ml={4} color="text.secondary">
                    ({t("global.cannot_be_combined", "Cannot be combined with the other types")})
                  </Typography>
                </>
              )}

              {(formik.values.usesDate || formik.values.usesDistance) && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 4, fontStyle: "italic" }}>
                  {t("checks.not_available_all_choosen", "Not available when manual intervals are selected")}
                </Typography>
              )}
              {formik.values.usesInspection && (
                <Box sx={{ ml: 4 }}>
                  <Typography variant="bodySmall" color="textSecondary">
                    {t("checks.inspection_type_desc", inspectionTypeDesc)}
                  </Typography>
                </Box>
              )}
            </Grid>
          </ListItem>
        </List>
      </FormGroup>

    </Box>
  )
}
